import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckCircle, KeyRound, MapPin, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OrderedJobStepperCard
 * ---------------------
 * Site Agent's building-by-building stepper for TODAY. Renders the payload
 * from GET /api/ops/ordered-jobs/me as a numbered stepper with gate codes
 * and per-building entry codes surfaced up-front.
 */
export function OrderedJobStepperCard() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doneIds, setDoneIds] = useState({}); // key = `${property_id}::${building_number}` → true

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/ops/ordered-jobs/me`, { credentials: 'include' });
        const data = res.ok ? await res.json() : { cards: [] };
        setCards(data.cards || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const markDone = (pid, bn) => setDoneIds((d) => ({ ...d, [`${pid}::${bn}`]: true }));

  if (loading) return null;
  if (!cards.length) return null;

  return (
    <Card
      className="bg-zinc-900 border-2 p-4 sm:p-6"
      style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
      data-testid="ordered-job-stepper-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <MapPin className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
          TODAY&rsquo;S ORDERED JOB CARD
        </h3>
        <Badge className="bg-[#FFD60A] text-black text-[10px]">
          {cards.length} propert{cards.length === 1 ? 'y' : 'ies'}
        </Badge>
      </div>

      <div className="space-y-4">
        {cards.map((card) => (
          <div
            key={card.property_id}
            className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3"
            data-testid={`stepper-property-${card.property_id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">{card.property_name}</p>
                {card.address && <p className="text-[11px] text-zinc-400 truncate">{card.address}{card.city ? ` · ${card.city}` : ''}</p>}
              </div>
              {(card.gate_entry_codes || []).length > 0 && (
                <div
                  className="flex items-center gap-1 px-2 py-1 rounded bg-[#C9A962]/20 border border-[#C9A962]/50"
                  data-testid={`stepper-gate-codes-${card.property_id}`}
                >
                  <KeyRound className="h-3.5 w-3.5" style={{ color: '#C9A962' }} />
                  <span className="text-[11px] text-[#C9A962] font-mono">
                    Gate: {card.gate_entry_codes.join(' / ')}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center overflow-x-auto pb-1 gap-1.5">
              {card.buildings.map((b, i) => {
                const done = doneIds[`${card.property_id}::${b.building_number}`];
                return (
                  <div key={b.building_number} className="flex items-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => markDone(card.property_id, b.building_number)}
                      className={`min-w-[8rem] p-2 rounded-md border text-left ${done ? 'bg-emerald-500/20 border-emerald-500/60' : 'bg-zinc-900 border-zinc-700 hover:border-[#FFD60A]'}`}
                      data-testid={`stepper-step-${card.property_id}-${b.building_number}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Badge
                          className={done ? 'bg-emerald-500 text-black text-[10px]' : 'bg-[#FFD60A] text-black text-[10px]'}
                        >
                          {done ? <CheckCircle className="h-3 w-3" /> : b.order ?? i + 1}
                        </Badge>
                        <span className="text-xs text-white font-semibold">
                          Bldg {b.building_number}
                        </span>
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-1">
                        {(b.units || []).length} units
                      </p>
                      {b.entry_code && (
                        <p className="text-[10px] font-mono text-[#C9A962] mt-0.5" data-testid={`stepper-entry-${card.property_id}-${b.building_number}`}>
                          🔑 {b.entry_code}
                        </p>
                      )}
                    </button>
                    {i < card.buildings.length - 1 && (
                      <ChevronRight className="h-4 w-4 text-zinc-600 mx-1 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDoneIds({})}
          className="border-zinc-600 text-zinc-300 text-xs"
          data-testid="stepper-reset"
        >
          Reset check-offs
        </Button>
      </div>
    </Card>
  );
}

export default OrderedJobStepperCard;
